import store from 'redux/store';
import * as actions from 'redux/constants';
import {
  setMerchantData,
  setGeoData,
  setABTestingData,
  setPageToken,
  setFeatureFlags,
  hideBanner,
  setPageCommonData
} from 'redux/actions/global';
import { isEmpty, get, set, cloneDeep } from 'lodash-es';
import { sanitizeInput } from 'services/sanitize';
import localStorageService from 'services/localStorage';
import {
  sendParentMessage,
  sendLoadingEvent,
  iframeMessages
} from 'services/iframeService';
import { getHostedPageType } from 'utilities/global';
import { asyncChangeLanguage } from 'services/i18n';
import { sendPreviewLoaded } from 'components/containers/Preview/HostedPagePreview';
import {
  GET_SUBSCRIPTION_PAGE_FAIL,
  GET_SUBSCRIPTION_PAGE_SUCCESS,
  CREATE_SUBSCRIPTION_PAYMENT_START
} from 'redux/constants/subscription';
import {
  AUTHORIZE_PAYMENT_METHOD_START,
  CREATE_BENEFICIARY_START,
  CREATE_PAYMENT_START,
  CREATE_UPDATE_CUSTOMER_START,
  DELETE_CARD_START,
  EDIT_CARD_START,
  TOKENIZE_CARD_START,
  VERIFY_DOCUMENTS_START,
  VERIFY_OTP_START
} from 'redux/constants';
import {
  GET_PAYOUT_TRACKER_PAGE_FAIL,
  GET_PAYOUT_TRACKER_PAGE_SUCCESS
} from 'redux/constants/payoutTracker';
import initGoogleAnalytics from 'services/googleAnalytics';

const changeAppLanguage = ({ data }) => {
  const { id: pageToken, changeLanguage, language } = data || {};
  const isLanguageInLocalStorage = localStorageService.getItem(`${pageToken}_lang`);
  const shouldChangeLanguage = isLanguageInLocalStorage ? false : changeLanguage ?? true;

  if (shouldChangeLanguage && language) {
    asyncChangeLanguage(language);
  }
};

const sanitizeData = ({ data }) => {
  const clonedData = cloneDeep(data);
  // List of known keys that should be sanitized
  const keysToSanitize = [
    'cancel_url',
    'complete_url',
    'merchant_website',
    'cancel_checkout_url',
    'complete_checkout_url',
    'payment.redirect_url',
    'payment.complete_payment_url',
    'payment.error_payment_url',
    'payment_complete_url',
    'payment_error_url',
    'redirect_url',
    'complete_payment_url',
    'error_payment_url'
  ];

  keysToSanitize.forEach(key => {
    const currentValue = get(clonedData, key);
    if (!currentValue) return;

    const sanitizedValue = sanitizeInput(currentValue);
    set(clonedData, key, sanitizedValue);
  });

  return clonedData;
};

const saveCommonData = ({ data }) => {
  const {
    id: pageToken,
    language,
    org_id: orgId,
    merchant_color: merchantColor,
    merchant_logo: merchantLogo,
    merchant_logo_base64: merchantLogoBase64,
    merchant_website: merchantWebsite,
    merchant_alias: merchantAlias,
    merchant_customer_support: merchantCustomerSupport,
    merchant_terms: merchantTerms,
    merchant_privacy_policy: merchantPrivacyPolicy,
    merchant_main_button: merchantMainButton,
    cancel_url: cancelUrl,
    complete_url: completeUrl,
    geo_country: geoCountry,
    region,
    country: pageCountry
  } = data || {};

  const { global } = store.getState();
  const { isPreview, previewConfig } = global;

  const logo = isPreview && previewConfig.logo ? previewConfig.logo : merchantLogo;

  if (pageToken) {
    store.dispatch(setPageToken(pageToken));
  }

  store.dispatch(
    setMerchantData({
      orgId,
      website: merchantWebsite,
      logo,
      logo_base64: merchantLogoBase64,
      color: merchantColor,
      alias: merchantAlias,
      customerSupport: {
        email: merchantCustomerSupport?.email,
        phoneNumber: merchantCustomerSupport?.phone_number,
        url: merchantCustomerSupport?.url
      },
      language,
      terms: merchantTerms,
      privacy_policy: merchantPrivacyPolicy,
      mainButton: merchantMainButton,
      cancelUrl,
      completeUrl
    })
  );
  store.dispatch(
    setGeoData({
      geoCountry,
      region
    })
  );
  store.dispatch(
    setPageCommonData({
      pageCountry
    })
  );
};

const handleABTestData = ({ data }) => {
  const { id: pageToken, ab_testing_variants: ABTestData } = data || {};
  if (!pageToken || !ABTestData) return;

  const existingABTestsData = localStorageService.getParsedItem(`${pageToken}_ab_tests`);
  if (isEmpty(existingABTestsData)) {
    localStorageService.setItem(`${pageToken}_ab_tests`, JSON.stringify(ABTestData));
  }

  store.dispatch(setABTestingData(ABTestData));
};

const handleReceivedFeatureFlags = ({ data = {} }) => {
  store.dispatch(setFeatureFlags(data));
};

const handlePreview = () => {
  const { global } = store.getState();
  const { isPreview } = global;

  if (isPreview) {
    sendPreviewLoaded();
  }
};

const globalMiddleware = store => next => action => {
  switch (action.type) {
    case actions.SET_IS_LOADING: {
      const notifySdk = action?.meta?.notifySdk ?? true;
      const pageType = action?.meta?.pageType || getHostedPageType();
      if (!notifySdk) break;
      sendLoadingEvent({ pageType, isLoading: action.payload.loading });
      break;
    }
    case actions.CLOSE_IFRAME:
      sendParentMessage(iframeMessages.CLOSE_IFRAME, null);
      break;
    case actions.GET_CHECKOUT_PAGE_SUCCESS:
    case actions.GET_CHECKOUT_PAGE_FAIL:
    case actions.GET_BENEFICIARY_TOKENIZATION_PAGE_SUCCESS:
    case actions.GET_BENEFICIARY_TOKENIZATION_PAGE_FAIL:
    case actions.GET_CARD_TOKENIZATION_PAGE_SUCCESS:
    case actions.GET_CARD_TOKENIZATION_PAGE_FAIL:
    case actions.GET_ISSUED_PAGE_DATA_FAIL:
    case actions.GET_ISSUED_PAGE_DATA_SUCCESS:
    case actions.GET_IDV_PAGE_FAIL:
    case actions.GET_IDV_PAGE_SUCCESS:
    case actions.GET_ACTIVATE_ISSUE_CARD_PAGE_SUCCESS:
    case actions.GET_ACTIVATE_ISSUE_CARD_PAGE_FAIL:
    case actions.GET_RESET_PIN_PAGE_SUCCESS:
    case actions.GET_RESET_PIN_PAGE_FAIL:
    case actions.GET_OTP_SUCCESS:
    case actions.GET_OTP_FAIL:
    case GET_SUBSCRIPTION_PAGE_SUCCESS:
    case GET_SUBSCRIPTION_PAGE_FAIL:
    case actions.GET_REUSABLE_LINK_PAGE_SUCCESS:
    case actions.GET_REUSABLE_LINK_PAGE_FAIL:
    case actions.GET_SHORTLINK_PAGE_FAIL:
    case actions.GET_CUSTOMER_TOKENIZATION_PAGE_SUCCESS:
    case actions.GET_CUSTOMER_TOKENIZATION_PAGE_FAIL:
    case GET_PAYOUT_TRACKER_PAGE_SUCCESS:
    case GET_PAYOUT_TRACKER_PAGE_FAIL: {
      const data = action.payload.data || action.payload;

      const sanitizedData = sanitizeData({ data });
      changeAppLanguage({ data: sanitizedData });
      saveCommonData({ data: sanitizedData });
      handleABTestData({
        data: sanitizedData
      });
      initGoogleAnalytics(sanitizedData);
      handlePreview();

      // Override action payload with data after sanitizing
      if (action.payload.data) {
        action.payload.data = sanitizedData;
      } else if (action.payload) {
        action.payload = sanitizedData;
      }

      if (sanitizedData.feature_flags) {
        const { feature_flags: featureFlags } = sanitizedData;
        handleReceivedFeatureFlags({ data: featureFlags });
      }

      break;
    }
    case VERIFY_DOCUMENTS_START:
    case VERIFY_OTP_START:
    case CREATE_SUBSCRIPTION_PAYMENT_START:
    case CREATE_PAYMENT_START:
    case AUTHORIZE_PAYMENT_METHOD_START:
    case DELETE_CARD_START:
    case EDIT_CARD_START:
    case TOKENIZE_CARD_START:
    case CREATE_BENEFICIARY_START:
    case CREATE_UPDATE_CUSTOMER_START: {
      store.dispatch(hideBanner());
      break;
    }
  }

  next(action);
};

export default globalMiddleware;
